import {
  ChangeEvent,
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { Cancel, CheckCircle, Error } from '@mui/icons-material';
import {
  Button as MuiButton,
  ButtonGroup,
  Divider,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';

import { useQueryClient } from '@tanstack/react-query';

import Alert from '@/components/alert';
import Button from '@/components/button';
import Card from '@/components/card';
import Dialog from '@/components/dialog';
import { SMALL_ROWS_PER_PAGE_OPTIONS } from '@/constants/table';
import { ViolationContext } from '@/contexts/ViolationContext';
import { useUpdateViolationOpinion } from '@/hooks/queries/violations';
import { useAppSelector } from '@/hooks/redux';
import useAlert from '@/hooks/useAlert';
import { Violations } from '@/models/violations';
import { formatDate } from '@/utils/datesAndTime';

import {
  complianceMapper,
  ComplianceStatus,
  formatAdType
} from '../../_partials/helper';

type OpinionType = 'agree' | 'disagree';

const getAlertContent = (violations: Violations.Violation[]) => {
  const title = violations
    .map((v) => v.compliance)
    .filter(Boolean)
    .map((v) => v.charAt(0).toUpperCase() + v.slice(1));

  const content = violations.map((v) =>
    v.adType === 'questionable'
      ? 'Possible Advertisement'
      : v.adType.charAt(0).toUpperCase() + v.adType.slice(1)
  );

  return {
    title: title[0] || '',
    content: content[0] || ''
  };
};

const getViolationsInfo = (violations: Violations.Details) => {
  if (violations.violations.length === 0) return { text: '', suffix: '' };

  const [firstViolation, ...otherViolations] = violations.violations;

  const text = `${firstViolation.author}, ${formatDate(
    new Date(firstViolation.createdAt),
    'dd/MM/yyyy'
  )}, ${firstViolation.evaluationName}`;

  const violationsCount = otherViolations.length;

  const violationsSuffix =
    violationsCount > 0
      ? ` + ${violationsCount} other rating${violationsCount > 1 ? 's' : ''}`
      : '';

  return { text, suffix: violationsSuffix };
};

const getWhyViolation = (
  violation: Violations.Violation,
  timeline: Violations.Timeline[] = []
) =>
  timeline.find(
    (entry) =>
      entry.type === 'comment' &&
      entry.author === violation.author &&
      Math.abs(
        new Date(entry.createdAt).getTime() -
          new Date(violation.createdAt).getTime()
      ) < 1000
  )?.content ?? '';

const OPINION_OPTIONS: OpinionType[] = ['agree', 'disagree'];

const ViolationRatingOpinion = () => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user.currentUser);
  const getComplianceInfo = (compliance: string) =>
    complianceMapper[compliance as ComplianceStatus] ||
    complianceMapper.unknown;

  const { postId, violationData, urlParams } = useContext(ViolationContext);

  const [secondOpinion, setSecondOpinion] = useState<boolean | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isEditing, setEditing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [author, setAuthor] = useState<{ name: string; date: string }>({
    name: '',
    date: ''
  });

  const { mutate: updateOpinion, isPending: isUpdating } =
    useUpdateViolationOpinion({
      onSuccess: () => {
        queryClient.setQueryData(
          ['violation', postId, urlParams],
          (oldData: Violations.Details) => ({
            ...oldData,
            secondOpinion: {
              opinion: secondOpinion,
              author: user.name,
              createdAt: new Date().toString()
            },
            timeline: [
              ...oldData.timeline,
              {
                type: 'opinion',
                author: user.name,
                content: secondOpinion?.toString(),
                createdAt: new Date().toString()
              }
            ]
          })
        );
        setEditing(false);
        alert.success('The second opinion was successfully updated');
      },
      onError: () =>
        alert.error('Some error occurred while adding second opinion')
    });

  const handleSecondOpinion = (type: OpinionType) => {
    setSecondOpinion(type === 'agree');
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleSetOpinion = () => {
    updateOpinion({
      postId,
      data: {
        opinion: secondOpinion
      }
    });
  };

  const handleChangePage = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const initializeOpinion = () => {
    setSecondOpinion(violationData?.secondOpinion.opinion ?? null);
    setAuthor({
      name: violationData?.secondOpinion.author ?? '',
      date: violationData?.secondOpinion.createdAt ?? ''
    });
  };

  const handleCancel = () => {
    initializeOpinion();
    setEditing(false);
  };

  const rows = useMemo(
    () =>
      violationData?.violations?.map((violation) => ({
        id: violation.evaluationId,
        evaluationName: violation.evaluationName,
        user: violation.author,
        compliance: violation.compliance,
        timeOfRating: formatDate(
          new Date(violation.createdAt),
          'dd/MM/yyyy HH:mm'
        ),
        adType: violation.adType,
        why: getWhyViolation(violation, violationData?.timeline)
      })) || [],
    [violationData?.violations]
  );

  useEffect(() => {
    initializeOpinion();
  }, [violationData]);

  const paginatedRows = useMemo(
    () => rows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const hasSecondOpinion = useMemo(
    () => Boolean(violationData?.secondOpinion?.author),
    [violationData?.secondOpinion]
  );

  if (!violationData)
    return <Typography variant="body2">Data not available</Typography>;

  return (
    <>
      <Card
        sx={{ marginBottom: 2 }}
        title="Rating"
        isCollapsible
        content={
          <Stack gap={2}>
            <Alert
              variant="outlined"
              sx={{
                color: 'text.primary',
                border: '2px solid #dadada'
              }}
              title={getAlertContent(violationData.violations).title}
              icon={<Error color="error" fontSize="large" />}
              content={
                <>
                  {getAlertContent(violationData.violations).content}
                  <Typography variant="body2">
                    <Typography
                      variant="body2"
                      component="span"
                      color="secondary"
                      onClick={() => setIsDialogOpen(true)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {getViolationsInfo(violationData).text}
                    </Typography>
                    {getViolationsInfo(violationData).suffix}
                  </Typography>
                </>
              }
            />
            <Divider sx={{ borderColor: 'primary.main' }} />
            {!hasSecondOpinion || isEditing ? (
              <>
                <Typography variant="body2">
                  Do you agree with the rating above?
                </Typography>
                <ButtonGroup
                  fullWidth
                  variant="outlined"
                  sx={{
                    flexWrap: 'wrap', // Allow buttons to wrap
                    '& .MuiButton-root': {
                      flex: '1 1 calc(33.333% - 8px)', // Make buttons responsive
                      textTransform: 'none',
                      fontSize: { xs: '0.7rem', sm: '0.8rem' }, // Adjust font size for small screens
                      minWidth: 100 // Ensure a minimum button width
                    }
                  }}
                >
                  {OPINION_OPTIONS.map((opinion) => (
                    <MuiButton
                      key={opinion}
                      onClick={() =>
                        handleSecondOpinion(opinion as OpinionType)
                      }
                      disabled={violationData.isExpired}
                      variant={
                        secondOpinion === (opinion === 'agree')
                          ? 'contained'
                          : 'outlined'
                      }
                    >
                      {opinion.charAt(0).toUpperCase() + opinion.slice(1)}
                    </MuiButton>
                  ))}
                </ButtonGroup>
              </>
            ) : (
              <>
                <Alert
                  variant="outlined"
                  sx={{
                    color: 'text.primary',
                    border: '2px solid #dadada'
                  }}
                  title={secondOpinion ? 'Agree' : 'Disagree'}
                  icon={
                    secondOpinion ? (
                      <CheckCircle color="success" fontSize="large" />
                    ) : (
                      <Cancel color="error" fontSize="large" />
                    )
                  }
                  content={`${author?.name} - ${formatDate(
                    author?.date,
                    'dd/MM/yyyy'
                  )}`}
                />
              </>
            )}
          </Stack>
        }
        actions={
          !hasSecondOpinion || isEditing ? (
            <>
              {isEditing && (
                <Button
                  variant="text"
                  text="cancel"
                  disabled={isUpdating}
                  onClick={handleCancel}
                />
              )}
              <Button
                size="small"
                disabled={
                  secondOpinion === null ||
                  secondOpinion === violationData?.secondOpinion.opinion
                }
                text={!hasSecondOpinion ? 'give opinion' : 'save opinion'}
                loading={isUpdating}
                onClick={handleSetOpinion}
              />
            </>
          ) : (
            <Button
              size="small"
              text="edit"
              onClick={() => setEditing(true)}
              disabled={violationData.isExpired}
            />
          )
        }
      />
      <Dialog
        maxWidth="lg"
        fullWidth
        open={isDialogOpen}
        title="Users"
        dialogContent={
          <TableContainer component={Stack}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Compliance</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    Evaluation name
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    Time of rating
                  </TableCell>
                  <TableCell>Why?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map((violation) => (
                  <TableRow key={violation.id}>
                    <TableCell>{violation.user}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={1}>
                        {getComplianceInfo(violation.compliance).icon}
                        <Typography>
                          {getComplianceInfo(violation.compliance).text}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{formatAdType(violation.adType)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {violation.evaluationName}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {violation.timeOfRating}
                    </TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>
                      {violation.why || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={SMALL_ROWS_PER_PAGE_OPTIONS}
            />
          </TableContainer>
        }
        onClose={handleCloseDialog}
        actions={<Button text="close" onClick={handleCloseDialog} />}
      />
    </>
  );
};

export default ViolationRatingOpinion;
